import { cva } from 'class-variance-authority'
import { type HTMLAttributes, type ReactNode, useId } from 'react'
import { cn } from '../../lib/cn'

/**
 * Info field (445:46461): a read-only caption over its value, e.g. "Market" over
 * "México". The caption is Label in an unbound grey (#6A7282, `gray-500`), not
 * Light dark; the value is Body in Dark.
 *
 * The component's `flag` property puts a 16px flag on its own row between the
 * caption and the value, and screens built from it (463:50248) render it that
 * way. Hand-built fields elsewhere put the flag before the value on one row
 * (445:45908); this component follows the component.
 */
export const infoFieldVariants = cva('flex min-w-0 flex-col items-start gap-1 font-atlas-sans')

export interface InfoFieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
	/** The caption, e.g. "Market". */
	label: string
	/** The value, e.g. "México". */
	value: ReactNode
	/** A 16px flag (or other mark) for the value. It is data, such as the market's country, so the caller supplies it. */
	flag?: ReactNode
}

/** A caption and its value, grouped and named by the caption for assistive technology. */
export function InfoField({ label, value, flag, className, ...props }: InfoFieldProps) {
	const labelId = useId()
	return (
		<div {...props} role="group" aria-labelledby={labelId} className={cn(infoFieldVariants(), className)}>
			<span id={labelId} className="w-full text-atlas-label text-atlas-gray-500">
				{label}
			</span>
			{flag ? <span className="flex size-4 shrink-0 items-center justify-center overflow-hidden">{flag}</span> : null}
			<span className="w-full text-atlas-body text-atlas-dark">{value}</span>
		</div>
	)
}
